/* global PIXI TimelineMax TweenMax */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Background from '../frames/_common/Background.jsx';

import spritesHelper from '../../lib/spritesHelper';
import PixiFlux from '../../lib/pixi-flux';

import { isMobile } from '../../lib/isMobile';

const mobile = isMobile();

const propTypes = {
    rendererType: PropTypes.string.isRequired,
    children: PropTypes.node,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,

    index: PropTypes.number.isRequired,
    play: PropTypes.bool.isRequired,
    focus: PropTypes.bool.isRequired,
    visible: PropTypes.bool,
    pixiVisible: PropTypes.bool,
    isNext: PropTypes.bool.isRequired,
    device: PropTypes.string.isRequired,

    texts: PropTypes.array,
    animateTexts: PropTypes.bool,
    originalWidthOverride: PropTypes.number,
    fluxRefWidth: PropTypes.number,
    fluxRefHeight: PropTypes.number,
    fluxSettings: PropTypes.object,
    hideWhenPaused: PropTypes.bool,
    hideWhenOffscreen: PropTypes.bool,
    shadeWhenPaused: PropTypes.bool,
    className: PropTypes.string,

    onBuild: PropTypes.func,
    onPlay: PropTypes.func,
    onPause: PropTypes.func,
    onStartRender: PropTypes.func,
    onStopRender: PropTypes.func,
    onDestroy: PropTypes.func,

    onIntensityChanged: PropTypes.func,
    onMouseMove: PropTypes.func,

    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    onTick: PropTypes.func,
    onAfterBuild: PropTypes.func,

    animationDuration: PropTypes.number,
    animationDelay: PropTypes.number,

    cropYOffset: PropTypes.number,

    isTransitionning: PropTypes.bool,
};

const defaultProps = {
    children: null,
    visible: false,
    pixiVisible: false,
    texts: [],
    originalWidthOverride: null,
    animateTexts: true,
    fluxRefWidth: 450,
    fluxRefHeight: 800,
    fluxSettings: null,
    animationDuration: 0.6,
    animationDelay: 0.6,
    hideWhenPaused: false,
    hideWhenOffscreen: true,
    shadeWhenPaused: true,
    className: null,

    onBuild: null,
    onPlay: null,
    onPause: null,
    onStartRender: null,
    onStopRender: null,
    onDestroy: null,

    onBlur: null,
    onFocus: null,
    onTick: null,
};

const contextTypes = {
    originalWidth: PropTypes.number,
    originalHeight: PropTypes.number,
    events: PropTypes.object,
    cleanTint: PropTypes.number,
    dirtyTints: PropTypes.array,
};

class Frame extends Component {

    constructor(props) {
        super(props);

        this.onMouseMoved = this.onMouseMoved.bind(this);
        this.onVisibleChanged = this.onVisibleChanged.bind(this);
        this.onTick = this.onTick.bind(this);

        this.container = null;
        this.textsContainer = null;
        this.timeline = null;
        this.pixiFlux = null;
        this.mask = null;

        this.i = 0;

        this.textsRef = {};
    }

    /*
        Life cycle
    */

    componentDidMount() {
        const props = this.props;

        const container = new PIXI.Container();
        this.container = container;
        container.x = props.index * (props.width + props.border);
        container.name = `frame${props.index}`;
        this.container.interactive = false;
        this.container.interactiveChildren = false;
        props.container.addChild(container);

        // Each frame has a shadowy mask
        const mask = new PIXI.Graphics();
        this.mask = mask;
        props.container.addChild(mask);

        mask.clear();
        mask.beginFill(0);
        mask.drawRect(container.x - 1, 0, props.width + 2, props.fullHeight + 1);
        mask.endFill();

        if (props.storyHeight < props.height) {
            // console.log('cropped', props.height - props.storyHeight, container.position, props.cropYOffset);
            container.y = props.cropYOffset;
        }

        if (props.hideWhenOffscreen) {
            // console.log('visible', props.visible);
            container.visible = props.visible;
        }

        // console.log('sh', 'h', props.storyHeight, props.height);

        const timeline = new TimelineMax();
        this.timeline = timeline;

        if (props.fluxSettings !== null) {
            // const fluxRefWidth = Math.min(450, Math.max(Math.round(props.fluxRefWidth / 1.4), 360));
            // const fluxRefHeight = Math.min(800, Math.max(Math.round(props.fluxRefHeight / 1.4), 640));

            const fluxRefWidth = props.fluxRefWidth;
            const fluxRefHeight = props.fluxRefHeight;

            const fluxBaseWidth = props.fluxRefWidth;
            const fluxBaseHeight = props.fluxRefHeight;

            const fluxSettings = _.extend({
                width: fluxRefWidth,
                height: fluxRefHeight,
                baseWidth: fluxBaseWidth,
                baseHeight: fluxBaseHeight,
                mouse: !mobile,
                mouseOffset: window.innerHeight - props.height,
            }, props.fluxSettings);

            // console.log('flux', props, fluxRefWidth, fluxRefHeight, window.innerHeight - (props.height));

            this.pixiFlux = new PixiFlux(fluxSettings);
        }

        if (props.onBeforeObjectsBuild) {
            props.onBeforeObjectsBuild(container, timeline, {
                pixiFlux: this.pixiFlux,
            });
        }

        if (props.onBuild) {
            props.onBuild(container, timeline, {
                pixiFlux: this.pixiFlux,
            }, this.container);
        }

        if (props.onAfterObjectsBuild) {
            props.onAfterObjectsBuild(container, timeline, {
                pixiFlux: this.pixiFlux,
            });
        }

        // texts
        const textsContainer = new PIXI.Container();
        this.textsContainer = textsContainer;
        container.addChild(this.textsContainer);

        if (props.focus) {
            this.focus(0);
        } else {
            this.blur(0);
        }

        this.updateIntensity();

        if (this.pixiFlux && props.pixiVisible) {
            this.pixiFlux.tick();
        }

        if (this.props.onTick && props.visible) {
            this.props.onTick();
        }

        if (this.props.onAfterBuild) {
            this.props.onAfterBuild();
        }
    }

    componentDidUpdate(prevProps) {
        const { play, intensity, visible, focus, width, height, isTransitionning } = this.props;
        const playChanged = prevProps.play !== play;

        // const renderChanged = prevProps.render !== props.render;
        const intensityChanged = prevProps.intensity !== intensity;
        const visibleChanged = prevProps.visible !== visible;
        const focusChanged = prevProps.focus !== focus;
        const sizeChanged = prevProps.width !== width || prevProps.height !== height;

        const isTransitionningChanged = prevProps.isTransitionning !== isTransitionning;

        if (isTransitionningChanged) {
            if (isTransitionning === true) {
                this.transitionning();
            }
        }

        // console.log('size', sizeChanged);
        // if (playChanged) {
        //     console.log('pc');
        //     this.props.onWindowResize();
        // }
        // console.log('update', play);

        // if (playChanged && focus) {
        //     if (play) {
        //         // this.focus();
        //     } else {
        //         // this.blur();
        //     }
        // }

        if (focusChanged) {
            if (focus) {
                this.focus();
            } else {
                this.blur();
            }
        }

        if (visibleChanged) {
            this.onVisibleChanged();
        }

        if (intensityChanged) {
            this.updateIntensity(prevProps.intensity);
        }

        // if (sizeChanged) {
        //     if (this.container) {
        //         // this.container.x = this.props.index * (this.props.width + this.props.border);
        //         // console.log('maksResize', this.container.scale, this.props.width, this.props.fullHeight);
        //         // this.mask.clear();
        //         // this.mask.beginFill(0, 0.9);
        //         // this.mask.drawRect(this.container.x - 1, 0, this.props.width + 1, this.props.fullHeight);
        //         // this.mask.endFill();
        //     }
        // }
    }

    componentWillUnmount() {
        const props = this.props;

        this.pause();
        if (props.onDestroy) {
            props.onDestroy();
        }

        if (this.timeline) {
            this.timeline.removeCallback();
            this.timeline.kill();
            this.timeline = null;
        }

        if (this.container) {
            props.container.removeChild(this.container);
            this.container.destroy({
                children: true,
                // texture: true,
                // baseTexture: true,
            });
        }
        this.container = null;

        if (this.pixiFlux) {
            this.pixiFlux.resetShapes();
            this.pixiFlux.destroy();
        }

        this.pixiFlux = null;
    }

    onMouseMoved(opts) {
        const props = this.props;

        if (this.pixiFlux && props.focus) {
            const width = props.width;
            const height = props.height;
            const storyWidth = props.storyWidth;
            const storyHeight = props.storyHeight;

            const frameX = (storyWidth - width) / 2;
            const frameY = (storyHeight - height) / 2;

            const ratioX = (opts.event.clientX - frameX) / width;
            const ratioY = (opts.event.clientY - frameY) / height;

            let position = null;

            if (ratioX >= 0 || ratioX <= 1 || ratioY >= 0 || ratioY <= 1) {
                position = {
                    x: ratioX,
                    y: ratioY,
                };
            }

            if (this.pixiFlux && position !== null) {
                this.pixiFlux.updateMouseDownPosition(position);
            }
        }

        if (this.props.onMouseMove) {
            this.props.onMouseMove();
        }
    }

    onVisibleChanged() {
        if (this.props.hideWhenOffscreen && this.container !== null) {
            this.container.visible = this.props.visible;
        }

        if (this.props.visible) {
            // this.play();
        } else {
            // this.pause();
        }
    }

    onTick() {
        if (!this.props.focus) {
            return;
        }

        if (this.pixiFlux) {
            this.pixiFlux.tick();
        }

        if (this.props.onTick) {
            this.props.onTick();
        }
    }

    /*
        Methods
    */

    play() {
        this.timeline.play();
        if (this.props.hideWhenPaused) {
            this.container.visible = true;
        }
        if (this.props.onPlay) {
            this.props.onPlay();
        }
        this.context.events.on('tick', this.onTick);
        this.context.events.on('mousemove', this.onMouseMoved);
    }

    pause() {
        this.timeline.pause();
        if (this.props.hideWhenPaused) {
            this.container.visible = false;
        }
        if (this.props.onPause) {
            this.props.onPause();
        }
        this.context.events.off('tick', this.onTick);
        this.context.events.off('mousemove', this.onMouseMoved);
    }

    focus(duration) {
        const time = typeof duration !== 'undefined' ? duration : this.props.animationDuration;
        const delay = this.props.animationDelay;

        if (this.props.animateTexts) {
            TweenMax.killTweensOf(this.textsRef);
            TweenMax.to(this.textsRef, time, {
                alpha: 1,
                delay,
            });
        }

        if (this.mask !== null) {
            TweenMax.to(this.mask, time, {
                alpha: 0,
            });
        }

        if (this.props.onFocus) {
            this.props.onFocus(time, delay);
        }

        this.play();
    }

    blur(duration) {
        const { isNext, device } = this.props;
        const time = typeof duration !== 'undefined' ? duration : this.props.animationDuration;

        let alpha = 0.4;
        switch (device) {
        case 'phone':
            alpha = 0.6;
            break;
        case 'tablet':
            alpha = isNext ? 0.8 : 0.6;
            break;
        default:
            alpha = isNext ? 0.9 : 0.6;
        }

        if (this.props.animateTexts) {
            TweenMax.killTweensOf(this.textsContainer);
            TweenMax.killTweensOf(this.textsRef);
            TweenMax.to(this.textsRef, 0.1, {
                alpha: 0,
            });
        }

        if (this.mask !== null) {
            TweenMax.to(this.mask, time, {
                alpha,
            });
        }

        if (this.props.onBlur) {
            this.props.onBlur(time);
        }

        this.pause();
    }

    transitionning() {
        this.pause();
        if (this.props.animateTexts) {
            TweenMax.killTweensOf(this.textsContainer);
            TweenMax.killTweensOf(this.textsRef);
            TweenMax.to(this.textsRef, 0.1, {
                alpha: 0,
            });
        }
    }

    updateIntensity(prevIntensity) {
        const props = this.props;
        const context = this.context;

        if (this.pixiFlux && prevIntensity !== props.intensity) {
            this.pixiFlux.updateIntensity(props.intensity, {
                animate: props.focus,
                cleanTint: context.cleanTint,
                dirtyTints: context.dirtyTints,
            });
        }

        if (props.onIntensityChanged) {
            props.onIntensityChanged(prevIntensity);
        }
    }

    renderTexts() {
        const {
            cropYOffset,
            width,
            height,
        } = this.props;

        const texts = this.props.texts.map((text) => {
            let left;
            let right;

            const x = text.opts.croppedX && cropYOffset !== 0 ? text.opts.croppedX : text.opts.x;
            const y = text.opts.croppedY && cropYOffset !== 0 ? text.opts.croppedY : text.opts.y;

            if (text.opts.align === 'right') {
                left = null;
                right = text.opts ? (1080 - x) * ((width) / 1080) || 0 : 0;
            } else {
                left = text.opts ? x * (width / 1080) || 0 : 0;
                right = null;
            }

            const top = text.opts ? (y * (height / 1920)) + cropYOffset : 0;
            const maxedTop = top < 10 ? 10 : top;

            const style = {
                position: 'relative',
                top: maxedTop,
                left,
                right,
                textAlign: text.opts ? text.opts.align || 'left' : 'left',
                color: text.opts ? text.opts.color || '#000' : '#000',
                width: text.opts ? text.opts.width || 'auto' : 'auto',
            };

            const title = text.title ? text.title : '';
            const titleParts = title.split(/\n/g);

            const t = (
                <h4>
                    { titleParts.map((object, i) => {
                        if (i === titleParts.length - 1) {
                            return <span>{object}</span>;
                        }
                        return <span>{object}<br/></span>;
                    }) }
                </h4>
            );

            const subtitle = text.subtitle ? text.subtitle : '';
            const subtitleParts = subtitle.split(/\n/g);

            const s = (
                <p>
                    { subtitleParts.map((object, i) => {
                        if (i === subtitleParts.length - 1) {
                            return <span>{object}</span>;
                        }
                        return <span>{object}<br/></span>;
                    }) }
                </p>
            );

            return (
                <div className="frame-text-block" style={style} ref={(el) => { this.textsRef = el; }}>
                    { t }
                    { s }
                </div>
            );
        });

        return (
            <div className="frame-texts">
                {texts}
            </div>
        );
    }

    render() {
        const props = this.props;
        // const context = this.context;
        const className = this.props.className;
        const frameClassName = className ? `frame-${className}` : '';
        const formattedClassName = `frame ${frameClassName}`;
        // const left =
        // Math.floor(props.index * ((context.originalWidth * props.scale) + props.border));
        // const frameX = props.index * (props.width + props.border);

        const style = {
            position: 'absolute',
            width: props.frameWidth,
            height: props.frameHeight,
            // left: props.index * ((context.originalWidth + props.border) * (1 / props.scale)),
            // left: framesX,
            // transform: 'translateX('+frameX+'px)',
            top: 0,
            left: props.index * (props.width + props.border),
        };

        return (
            <div className={formattedClassName} style={style}>
                <Background {...this.props} />
                { this.renderTexts() }
            </div>
        );
    }
}

Frame.propTypes = propTypes;
Frame.defaultProps = defaultProps;
Frame.contextTypes = contextTypes;

module.exports = Frame;



// WEBPACK FOOTER //
// ./js/components/views/Frame.jsx